/**
 * PhotoCard — displays an approved unit photo with drag handle, caption, and delete.
 * Used inside the DnD sortable context.
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, MoreVertical, Star, Trash2 } from 'lucide-react';
import type { UnitPhoto } from '../../types/unit-marketing.types';

const AI_CATEGORY_LABELS: Record<string, string> = {
  interior: 'Interior',
  exterior: 'Exterior',
  floor_plan: 'Floor Plan',
};

// Matches Tailwind's default `md:` breakpoint used throughout this card to
// gate the mobile-only disclosure toggle/panel from the desktop hover-reveal
// behavior.
const MOBILE_BREAKPOINT_QUERY = '(min-width: 768px)';

interface Props {
  photo: UnitPhoto;
  onDelete: (photoId: string) => Promise<void>;
  onUpdateCaption: (photoId: string, caption: string | null) => Promise<void>;
  isPrimary: boolean;
  onSetPrimary: (photoId: string) => Promise<void>;
}

export function PhotoCard({ photo, onDelete, onUpdateCaption, isPrimary, onSetPrimary }: Props) {
  const [caption, setCaption] = useState<string>(photo.caption ?? '');
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isSettingPrimary, setIsSettingPrimary] = useState(false);
  const [isActionsExpanded, setIsActionsExpanded] = useState(false);
  const captionRef = useRef<string>(caption);
  captionRef.current = caption;
  const panelRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Tracks whether we're below the `md:` breakpoint, where the panel is
  // JS-gated by isActionsExpanded. At/above `md:`, the panel's reveal is
  // driven purely by CSS (:hover/:focus-within) and must stay keyboard-
  // reachable at all times (AC-7) — the JS-driven inert/tabbability gating
  // below only ever applies on mobile.
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => !window.matchMedia(MOBILE_BREAKPOINT_QUERY).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_BREAKPOINT_QUERY);
    const handleChange = () => setIsMobileViewport(!mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  // Mobile disclosure panel: collapse on any tap/click outside the panel and
  // its toggle button. Scoped strictly to these two refs (mirroring
  // UnmatchedTransactionsFeed's outside-click idiom) so it never intercepts
  // the drag handle's PointerSensor/KeyboardSensor activation elsewhere on
  // the card.
  useEffect(() => {
    if (!isActionsExpanded) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || toggleRef.current?.contains(target)) {
        return;
      }
      setIsActionsExpanded(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [isActionsExpanded]);

  // FM2 fix: on mobile, while the panel is collapsed it must not be
  // reachable by Tab — otherwise tabbing straight into the caption input
  // forces the browser's native focus-into-view behavior to reveal the
  // panel over the image even though aria-expanded is still "false". `inert`
  // removes the whole subtree from the tab order and the accessibility tree
  // (and, per spec, moves focus off it if focus was already inside), so it
  // both prevents the accidental reveal and self-corrects if state changes
  // out from under an already-focused control (e.g. handleSetPrimary
  // collapsing the panel after a successful set-primary). On/above `md:`
  // the panel must stay reachable so desktop's existing :focus-within
  // keyboard reveal (AC-7) keeps working.
  const isPanelReachable = isActionsExpanded || !isMobileViewport;
  useLayoutEffect(() => {
    if (panelRef.current) {
      panelRef.current.inert = !isPanelReachable;
    }
  }, [isPanelReachable]);

  // FM2 fix (part 2): once expanded on mobile, tabbing away from the card
  // entirely (e.g. past Delete into the next card's drag handle) must also
  // collapse the panel — the outside-click handler above only covers
  // mouse/touch, not keyboard focus leaving the card.
  const handleCardBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!isActionsExpanded) return;
    const next = e.relatedTarget;
    if (next && e.currentTarget.contains(next)) return;
    setIsActionsExpanded(false);
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : undefined,
  };

  const handleCaptionBlur = async () => {
    const val = captionRef.current.trim() || null;
    if (val !== (photo.caption ?? null)) {
      await onUpdateCaption(photo.id, val);
    }
  };

  const handleDeleteRequest = () => {
    setConfirmingDelete(true);
  };

  const handleDeleteCancel = () => {
    setConfirmingDelete(false);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      setConfirmingDelete(false);
      await onDelete(photo.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSetPrimary = async () => {
    // FM2 fix (cycle 2): move focus off this button *before* the upcoming
    // `setIsSettingPrimary(true)` disables it. A focused control that
    // becomes `disabled` is blurred to <body> by the browser synchronously,
    // on the very next commit — well before this function's `await` even
    // resolves, so checking focus in the `finally` block below is too late
    // (activeElement is already <body> by then). Redirecting focus here,
    // while it's still on this (not-yet-disabled) button, keeps a keyboard
    // user anchored on the still-visible, still-focusable toggle instead of
    // losing their place. This also covers the later collapse-to-`inert`
    // path (see the `isPanelReachable` useLayoutEffect above), since focus
    // is already outside the panel by the time that runs.
    if (panelRef.current?.contains(document.activeElement)) {
      toggleRef.current?.focus();
    }
    try {
      setIsSettingPrimary(true);
      await onSetPrimary(photo.id);
    } finally {
      setIsSettingPrimary(false);
      setIsActionsExpanded(false);
    }
  };

  const thumbnailSrc = photo.thumbnailUrl ?? photo.url ?? '';

  // Distinguishing identifier folded into each action's accessible name so a
  // screen-reader/keyboard user tabbing through the grid can tell which photo
  // a given control targets (AC-3) instead of hearing the same name repeated.
  // Always folds in displayOrder — the AI category label alone (e.g. "Interior")
  // is not unique when multiple approved photos share a category, which would
  // otherwise give two different photos the identical accessible name.
  const trimmedCaption = caption.trim();
  const photoIdentifier = trimmedCaption
    ? `"${trimmedCaption}"`
    : photo.aiCategory && AI_CATEGORY_LABELS[photo.aiCategory]
      ? `the ${AI_CATEGORY_LABELS[photo.aiCategory]} photo (photo ${photo.displayOrder + 1})`
      : `photo ${photo.displayOrder + 1}`;

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, aspectRatio: '4/3' }}
      data-testid={`photo-card-${photo.id}`}
      className="relative rounded-lg border border-border overflow-hidden bg-muted group"
      onBlur={handleCardBlur}
    >
      {/* Thumbnail */}
      <img
        src={thumbnailSrc}
        alt={photo.caption ?? 'Unit photo'}
        className="w-full h-full object-cover"
        loading="lazy"
      />

      {/* Primary + category badges — stacked top-left, clear of the drag handle at top-right */}
      <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
        {isPrimary && (
          <div
            data-testid={`photo-primary-badge-${photo.id}`}
            className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary text-primary-foreground"
          >
            Primary
          </div>
        )}
        {photo.aiCategory && AI_CATEGORY_LABELS[photo.aiCategory] && (
          <div className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-background/80 backdrop-blur-sm text-foreground">
            {AI_CATEGORY_LABELS[photo.aiCategory]}
          </div>
        )}
      </div>

      {/* Drag handle — always visible on mobile, hover-reveal on desktop */}
      <button
        type="button"
        data-testid={`photo-drag-handle-${photo.id}`}
        className="absolute top-2 right-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded bg-background/80 backdrop-blur-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 focus:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none"
        aria-label={`Drag to reorder ${photoIdentifier}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-foreground" />
      </button>

      {/* Mobile-only disclosure toggle — reveals the caption/actions panel on
          tap instead of it obscuring the thumbnail by default. Positioned
          left of the drag handle so it never overlaps the bottom panel. */}
      <button
        type="button"
        ref={toggleRef}
        onClick={() => setIsActionsExpanded(v => !v)}
        aria-expanded={isActionsExpanded}
        aria-controls={`photo-actions-panel-${photo.id}`}
        aria-label={isActionsExpanded ? `Hide actions for ${photoIdentifier}` : `Show actions for ${photoIdentifier}`}
        data-testid={`photo-actions-toggle-${photo.id}`}
        className="md:hidden absolute top-2 right-14 min-w-[44px] min-h-[44px] flex items-center justify-center rounded bg-background/80 backdrop-blur-sm text-foreground"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {/* Caption + delete overlay at bottom — tap-to-expand on mobile (kept
          unobstructive by default so the thumbnail is visible), hover-reveal
          on desktop (unconditioned by JS state — unchanged behavior). */}
      <div
        ref={panelRef}
        id={`photo-actions-panel-${photo.id}`}
        className={`absolute bottom-0 left-0 right-0 bg-background/80 backdrop-blur-sm px-2 py-1.5 transition-transform ${isActionsExpanded ? 'translate-y-0' : 'translate-y-full'} md:translate-y-full md:group-hover:translate-y-0 md:focus-within:translate-y-0`}
      >
        {confirmingDelete ? (
          <div className="flex items-center gap-2 py-0.5" data-testid={`photo-delete-confirm-${photo.id}`}>
            <span className="text-xs text-foreground flex-1">Delete this photo?</span>
            <button
              type="button"
              onClick={handleDeleteCancel}
              data-testid={`photo-delete-cancel-${photo.id}`}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center px-2 text-xs text-foreground hover:underline"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleDeleteConfirm()}
              disabled={isDeleting}
              data-testid={`photo-delete-confirm-action-${photo.id}`}
              className="min-w-[44px] min-h-[44px] flex items-center justify-center px-2 text-xs text-destructive font-semibold hover:underline disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <input
              type="text"
              value={caption}
              onChange={e => setCaption(e.target.value)}
              onBlur={handleCaptionBlur}
              aria-label="Photo caption"
              placeholder="Add a caption…"
              maxLength={500}
              data-testid={`photo-caption-${photo.id}`}
              // AC-5: the caption input is an interactive mobile control and
              // must keep the same 44px minimum touch target as every other
              // control on the card, not just its own text line height.
              className="w-full min-h-[44px] text-xs bg-transparent border-0 border-b border-input focus:outline-none focus:border-primary text-foreground placeholder-muted-foreground py-0.5"
            />
            {/* Action row on its own line so the icon buttons never have to
                share horizontal space with the caption input — at the
                narrowest supported card width (375px/2-col grid, ~165px
                card) two 44px buttons plus a flexed input overflowed the
                card edge. */}
            <div className="flex items-center justify-end gap-1">
              {!isPrimary && (
                <button
                  type="button"
                  onClick={() => void handleSetPrimary()}
                  disabled={isSettingPrimary}
                  data-testid={`photo-set-primary-${photo.id}`}
                  aria-label={`Set ${photoIdentifier} as primary photo`}
                  title={`Set ${photoIdentifier} as primary photo`}
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded text-foreground hover:bg-muted disabled:opacity-50 shrink-0"
                >
                  <Star className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={handleDeleteRequest}
                disabled={isDeleting}
                data-testid={`photo-delete-${photo.id}`}
                aria-label={`Delete ${photoIdentifier}`}
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded bg-destructive/90 transition-opacity disabled:opacity-50 shrink-0"
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive-foreground" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
